// The account signal of the multi-account detection (deployment.md §7.7,
// R1310).
//
// One person holds one account. After a sign-in, a guest creation and a
// world join, this module sends `POST /api/fairplay/signal` with three
// parts: the device id, the device fingerprint, and nothing for the
// network prefix, which the meta server reads off the request itself.
// The meta server stores every part as an HMAC and never the value.
//
// A store app sends the device id alone (deployment.md §7.7, R2927):
// Apple's rule 5.1.2 forbids a device fingerprint. The meta server takes
// a device with no other part and derives no fingerprint from it.
//
// The module decides nothing and shows nothing. A signal that fails is
// dropped without a word: a player must never meet a refusal here, and
// the next sign-in sends another one.
//
// Every storage read and write stands in a `try`: a private window, a
// blocked cookie jar and a thumbnail capture all throw.

import { FAIRPLAY_COLLECT_TIMEOUT_MS, FAIRPLAY_DEVICE_COOKIE_DAYS } from '../gen/balance';
import { cookieStore, storageStore, takeRenamed } from './renamed';
import { randomHex } from '../util/random';
import { storeApp } from '../platform/platform';
import { storeOf, writeStore } from '../ui/localstore';

/** The three moments a signal is sent. */
export type SignalEvent = 'signin' | 'guest' | 'join';

/** What the client sends about the device: the `device` member of the
 *  signal body, field for field the Go `fairplay.Device`. */
export interface DeviceReport {
  deviceIds: string[];
  platform: string;
  platformVersion: string;
  model: string;
  arch: string;
  userAgent: string;
  screen: string;
  timeZone: string;
  languages: string[];
  cores: number;
  memoryGb: number;
  webglVendor: string;
  webglRenderer: string;
  webgpu: string;
  canvas: string;
}

/** The first-party cookie and the storage key that hold the device id. */
export const DEVICE_COOKIE = 'ap_device';
export const DEVICE_STORAGE_KEY = 'ap.device';
/** The tab mark of the one `signin` signal at the hub boot. */
export const SIGNIN_MARK_KEY = 'ap.fairplay.signin';
/** The old names of the three, before the short form `ap` (R1676). A
 *  read moves a value under an old name to its new name. */
// compat(R1676, 2026-12-04)
export const OLD_DEVICE_COOKIE = 'aq_device';
export const OLD_DEVICE_STORAGE_KEY = 'aq.device';
export const OLD_SIGNIN_MARK_KEY = 'aq.fairplay.signin';

/** A device id is 32 hex digits, which is what `newDeviceId` makes. A
 *  stored value of another shape is somebody else's and is replaced. */
const DEVICE_ID_SHAPE = /^[0-9a-f]{32}$/;

// --- the device id -----------------------------------------------------

/** The storages the device id lives in. A test passes its own. */
export interface DeviceStores {
  readCookie(): string;
  writeCookie(value: string): void;
  readStorage(): string;
  writeStorage(value: string): void;
}

/** The browser's own cookie jar and `localStorage`. A read moves the
 *  device id from its old name to its new name (R1676). A blocked jar or
 *  storage throws here, and `deviceIds` holds every call in a `try`. */
export function browserStores(): DeviceStores {
  const maxAge = FAIRPLAY_DEVICE_COOKIE_DAYS * 24 * 60 * 60;
  const jar = () => cookieStore(document, maxAge, location.protocol === 'https:');
  return {
    // compat(R1676, 2026-12-04)
    readCookie: () => takeRenamed(jar(), DEVICE_COOKIE, OLD_DEVICE_COOKIE) ?? '',
    writeCookie: (value) => { jar().set(DEVICE_COOKIE, value); },
    // compat(R1676, 2026-12-04)
    readStorage: () => takeRenamed(storageStore(localStorage), DEVICE_STORAGE_KEY, OLD_DEVICE_STORAGE_KEY) ?? '',
    writeStorage: (value) => { localStorage.setItem(DEVICE_STORAGE_KEY, value); },
  };
}

/** A new random device id: 16 random bytes as hex. */
function newDeviceId(): string {
  return randomHex(16);
}

/**
 * The device ids of this browser, the cookie copy first. A store that
 * lost its copy takes the other one back; a browser with neither makes a
 * new id and writes it to both. The answer holds the distinct valid ids
 * that were there BEFORE the repair, so a person who cleared one store
 * still sends the id the other one kept.
 */
export function deviceIds(stores: DeviceStores, make: () => string = newDeviceId): string[] {
  const valid = (read: () => string) => {
    try {
      const v = read();
      return DEVICE_ID_SHAPE.test(v) ? v : '';
    } catch {
      return '';
    }
  };
  const write = (put: (v: string) => void, v: string) => {
    try {
      put(v);
    } catch { /* a blocked store keeps no copy */ }
  };
  const cookie = valid(stores.readCookie);
  const stored = valid(stores.readStorage);
  const keep = cookie || stored || make();
  if (cookie !== keep) write(stores.writeCookie, keep);
  if (!stored) write(stores.writeStorage, keep);
  const out = [keep];
  if (stored && stored !== keep) out.push(stored);
  return out;
}

// --- the device fingerprint ---------------------------------------------

/**
 * cyrb53, a 53-bit string hash, as 14 hex digits. It is not a secret
 * and not a guard: it only shortens the canvas picture to a value the
 * meta server hashes again under its pepper.
 */
export function hash53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const n = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return n.toString(16).padStart(14, '0');
}

/** The parts of the User-Agent Client Hints this module asks for. */
interface HighEntropy {
  platform?: string;
  platformVersion?: string;
  model?: string;
  architecture?: string;
}

interface UAData {
  getHighEntropyValues(hints: string[]): Promise<HighEntropy>;
}

/** The Client Hints, or nothing where the browser has none. */
async function clientHints(nav: Navigator): Promise<HighEntropy | null> {
  const ua = (nav as Navigator & { userAgentData?: UAData }).userAgentData;
  if (!ua || typeof ua.getHighEntropyValues !== 'function') return null;
  try {
    return await ua.getHighEntropyValues(['platform', 'platformVersion', 'model', 'architecture']);
  } catch {
    return null;
  }
}

/** The WebGL vendor and renderer, the unmasked pair where the browser
 *  gives it. The context is lost at once, so it holds no GPU memory. */
function webgl(doc: Document): { vendor: string; renderer: string } {
  try {
    const gl = doc.createElement('canvas').getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) return { vendor: '', renderer: '' };
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = String(gl.getParameter(dbg ? dbg.UNMASKED_VENDOR_WEBGL : gl.VENDOR) ?? '');
    const renderer = String(gl.getParameter(dbg ? dbg.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return { vendor, renderer };
  } catch {
    return { vendor: '', renderer: '' };
  }
}

interface GPUInfoLike { vendor?: string; architecture?: string; device?: string; description?: string }
interface GPULike { requestAdapter(): Promise<{ info?: GPUInfoLike } | null> }

/** The WebGPU adapter information, or nothing after the timeout. */
async function webgpu(nav: Navigator): Promise<string> {
  const gpu = (nav as Navigator & { gpu?: GPULike }).gpu;
  if (!gpu || typeof gpu.requestAdapter !== 'function') return '';
  const read = gpu.requestAdapter().then((adapter) => {
    const i = adapter?.info;
    return i ? [i.vendor, i.architecture, i.device, i.description].map((v) => v ?? '').join('|') : '';
  }).catch(() => '');
  const late = new Promise<string>((done) => setTimeout(() => done(''), FAIRPLAY_COLLECT_TIMEOUT_MS));
  return Promise.race([read, late]);
}

/** A small fixed drawing, hashed. Two devices with the same fonts, the
 *  same anti-aliasing and the same GPU draw the same pixels. */
function canvasHash(doc: Document): string {
  try {
    const c = doc.createElement('canvas');
    c.width = 240;
    c.height = 60;
    const g = c.getContext('2d');
    if (!g) return '';
    g.textBaseline = 'top';
    g.font = '16px sans-serif';
    g.fillStyle = '#f60';
    g.fillRect(100, 1, 62, 20);
    g.fillStyle = '#069';
    g.fillText('Aquaphobia \u{1F30A} 0.1', 2, 15);
    g.fillStyle = 'rgba(102, 204, 0, 0.7)';
    g.beginPath();
    g.arc(200, 30, 20, 0, Math.PI * 2);
    g.fill();
    const url = c.toDataURL();
    return url ? hash53(url) : '';
  } catch {
    return '';
  }
}

/** Reads every part of the device fingerprint. Each part that throws is
 *  left empty, so one refusal never costs the rest. */
export async function collectDevice(
  nav: Navigator = navigator,
  doc: Document = document,
  stores: DeviceStores = browserStores(),
): Promise<DeviceReport> {
  const [hints, gpu] = await Promise.all([clientHints(nav), webgpu(nav)]);
  const gl = webgl(doc);
  let timeZone = '';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch { /* no Intl zone */ }
  let screenText = '';
  try {
    screenText = `${screen.width}x${screen.height}@${window.devicePixelRatio ?? 1}`;
  } catch { /* no screen */ }
  return {
    deviceIds: deviceIds(stores),
    platform: hints?.platform ?? '',
    platformVersion: hints?.platformVersion ?? '',
    model: hints?.model ?? '',
    arch: hints?.architecture ?? '',
    // The User-Agent string stands in where the browser has no hints.
    userAgent: hints ? '' : (nav.userAgent ?? ''),
    screen: screenText,
    timeZone,
    languages: Array.from(nav.languages ?? []),
    cores: nav.hardwareConcurrency ?? 0,
    memoryGb: (nav as Navigator & { deviceMemory?: number }).deviceMemory ?? 0,
    webglVendor: gl.vendor,
    webglRenderer: gl.renderer,
    webgpu: gpu,
    canvas: canvasHash(doc),
  };
}

// --- the signal -------------------------------------------------------------

/** The device part of a signal: the whole report, or the device id
 *  alone in a store app. */
export type SignalDevice = DeviceReport | Pick<DeviceReport, 'deviceIds'>;

/** The device of a store app: the device id alone, no fingerprint part. */
export function idsOnlyDevice(stores: DeviceStores = browserStores()): Pick<DeviceReport, 'deviceIds'> {
  return { deviceIds: deviceIds(stores) };
}

let collected: Promise<SignalDevice> | null = null;

/** The device report of this page load, collected one time. */
function deviceOnce(): Promise<SignalDevice> {
  collected ??= storeApp() === null ? collectDevice() : Promise.resolve(idsOnlyDevice());
  return collected;
}

/**
 * Sends one account signal. It never throws and never waits for the
 * caller: the promise resolves to what the meta server said, or false.
 * `keepalive` lets the request finish when the page goes on to the game.
 */
export async function reportSignal(event: SignalEvent): Promise<boolean> {
  try {
    const device = await deviceOnce();
    const res = await fetch('/api/fairplay/signal', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, device }),
    });
    if (!res.ok) return false;
    const out = await res.json() as { recorded?: boolean };
    return out.recorded === true;
  } catch {
    return false;
  }
}

/**
 * Sends the `signin` signal one time in each browser tab for one account.
 * The hub calls it at its boot, because a sign-in with Apple or Google
 * ends in a redirect and never passes the call that sends the signal.
 */
export function reportSignInOnce(accountKey: string): void {
  // No tab storage: send it anyway.
  // compat(R1676, 2026-12-04)
  if (takeRenamed(storageStore(storeOf('session')), SIGNIN_MARK_KEY, OLD_SIGNIN_MARK_KEY) === accountKey) return;
  writeStore(SIGNIN_MARK_KEY, accountKey, 'session');
  void reportSignal('signin');
}

/** Forgets the collected report. Tests call it between cases. */
export function resetFairplayForTests(): void {
  collected = null;
}
